import { createAdminUserApi } from "@/api/services/admin/admin-user";
import { TCreateAdminUserReqDto } from "@/api/services/admin/admin-user/dtos/create-admin-user.dto";
import { Button } from "@/components/elements/Button";
import { DetailItem } from "@/components/elements/DetailItem";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { MenuNavigate, TMenuContent } from "@/components/elements/MenuNavigate/MenuNavigate";
import { Select } from "@/components/elements/Select";
import { StickyFooter } from "@/components/elements/StickyFooter/StickyFooter";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { createAdminUserSchema } from "@/features/system-settings/schema/createAdminUserSchema";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { divider, text } from "@/theme/colors";
import { setErrorForm, showError } from "@/utils/error";
import { yupResolver } from "@hookform/resolvers/yup";
import { Card, CardContent, CardHeader, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

const initValue: TCreateAdminUserReqDto = {
  fullName: "",
  email: "",
  role: "",
};

const AdminUserCreatePage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const methods = useForm<TCreateAdminUserReqDto>({
    defaultValues: initValue,
    resolver: yupResolver(createAdminUserSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });
  const { control } = methods;

  const onSubmit = async (data: TCreateAdminUserReqDto) => {
    try {
      await createAdminUserApi(data);
      toast.success(API_SUCCESS_MESSAGE);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ADMIN_USER.FETCH_LIST] });
      navigate(AppRoutes.systemSettingsAdminUsers);
    } catch (error) {
      setErrorForm(methods, error);
      showError(error);
    }
  };

  const handleBack = () => {
    navigate(AppRoutes.systemSettingsAdminUsers);
  };

  const contents: TMenuContent[] = useMemo(
    () => [
      {
        title: "ユーザーの状態",
        content: (
          <Card variant="outlined" sx={{ borderColor: divider.middle, borderRadius: "8px" }}>
            <CardHeader
              title={
                <Typography variant="head20Bold" color={text.primary}>
                  ユーザーの状態
                </Typography>
              }
              sx={{ pt: "24px", pb: 0, px: 3 }}
            />
            <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
              <Grid container spacing={3}>
                <Grid item xs={4.8}>
                  <DetailItem required label="ステータス" value="使用中" />
                </Grid>
                <Grid item xs={4.8}>
                  <DetailItem label="認証ステータス" value="登録メール送信前" />
                </Grid>
                <Grid
                  item
                  xs={2.4}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Button size="sm" variant="ghost" disabled>
                    パスワードリセット
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ),
      },
      {
        title: "基本情報",
        content: (
          <Card variant="outlined" sx={{ borderColor: divider.middle, borderRadius: "8px" }}>
            <CardHeader
              title={
                <Typography variant="head20Bold" color={text.primary}>
                  基本情報
                </Typography>
              }
              sx={{ pt: "24px", pb: 0, px: 3 }}
            />
            <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <FormField control={control} name="fullName">
                    <Input />
                  </FormField>
                </Grid>
                <Grid item xs={6}>
                  <FormField control={control} name="email">
                    <Input />
                  </FormField>
                </Grid>
                <Grid item xs={6}>
                  <FormField control={control} name="role">
                    <Select enumName="EAdminUserRole" />
                  </FormField>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ),
      },
    ],
    [control],
  );

  return (
    <Form height="100%" methods={methods} onSubmit={onSubmit} schema={createAdminUserSchema}>
      <Stack gap={3}>
        <Stack gap={1}>
          <Typography variant="head20Bold" color={text.primary}>
            管理ユーザーの新規追加
          </Typography>
          <Typography variant="body14" color={text.secondary}>
            現在のシステム管理画面にアクセスできるユーザーを作成します。送付されるメールにはURLが送付されるため、追加したユーザーに登録するようにお伝えください。
          </Typography>
        </Stack>

        <MenuNavigate
          contents={contents}
          contentAffix={
            <StickyFooter justifyContent="space-between">
              <Button size="sm" variant="ghost" onClick={handleBack}>
                戻る
              </Button>
              <Button size="sm" type="submit">
                メールを送信
              </Button>
            </StickyFooter>
          }
        />
      </Stack>
    </Form>
  );
};

export default AdminUserCreatePage;
